import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { getIcon } from "@/lib/icons";
import { Sparkles } from "lucide-react";

const CARD_BACK =
  "https://images.unsplash.com/photo-1669277928761-80ccecaa2b3f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxnb2xkJTIwY2VsZXN0aWFsJTIwbWFuZGFsYSUyMHRyYW5zcGFyZW50fGVufDB8fHx8MTc4Nzk2Mzc2MXww&ixlib=rb-4.1.0&q=85";

const FACE_BASE = "card-face absolute inset-0 rounded-xl bg-[#131422] p-2";

// ---------------- Image front: real artwork (tarot) or oracle illustration ----------------
const ImageFront = ({ card, reversed, deckType, imgSrc, onError }) => {
  const isOracle = deckType !== "tarot";
  return (
    <div className={FACE_BASE} style={{ transform: "rotateY(180deg)", border: "1px solid rgba(217,164,65,0.6)" }}>
      <div className="relative h-full w-full overflow-hidden rounded-lg" style={{ border: "1px solid rgba(217,164,65,0.35)" }}>
        <img
          src={imgSrc}
          alt={card.title || card.name}
          onError={onError}
          loading="lazy"
          draggable={false}
          className="h-full w-full object-cover"
          style={{ transform: !isOracle && reversed ? "rotate(180deg)" : "none" }}
        />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0B0C15] via-[#0B0C15]/70 to-transparent px-3 pb-2.5 pt-9 text-center">
          {isOracle && card.element && (
            <span className="mb-1 inline-block rounded-full border border-[#D9A441]/40 bg-[#0B0C15]/60 px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] text-[#E5C365]">
              {card.element}
            </span>
          )}
          <h3 className="font-display text-base font-semibold leading-tight text-[#F8F3E6]">
            {card.title || card.name}
          </h3>
          {isOracle && (
            <p className="mt-0.5 text-[9px] uppercase tracking-[0.15em] text-[#A3A5B8]">
              {(card.keywords || []).slice(0, 3).join(" · ")}
            </p>
          )}
          {!isOracle && reversed && (
            <span className="mt-1 inline-block rounded-full bg-[#722F37]/80 px-2 py-0.5 text-[9px] font-medium tracking-wide text-[#F8F3E6]">
              Reversed
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------- CSS/typography front (fallback when no image loads) ----------------
const CssFront = ({ card, reversed, deckType }) => {
  const Icon = getIcon(card.icon);
  const isOracle = deckType !== "tarot";
  const keywords = isOracle
    ? card.keywords
    : reversed
    ? card.keywords_reversed
    : card.keywords_upright;
  const topRight = isOracle ? card.element || "Oracle" : card.arcana;
  const themeWord = isOracle ? card.theme_keyword || (keywords && keywords[0]) : null;

  return (
    <div className={FACE_BASE} style={{ transform: "rotateY(180deg)", border: "1px solid rgba(217,164,65,0.55)" }}>
      <div
        className="relative flex h-full w-full flex-col items-center justify-between rounded-lg px-3 py-4 text-center"
        style={{
          border: "1px dashed rgba(217,164,65,0.45)",
          background: "radial-gradient(circle at 50% 30%, rgba(114,47,55,0.28) 0%, #10111d 65%)",
          transform: reversed ? "rotate(180deg)" : "none",
        }}
      >
        <div className="flex w-full items-center justify-between">
          <span className="font-display text-lg leading-none text-[#D9A441]">{card.roman ?? ""}</span>
          <span className="rounded-full border border-[#D9A441]/30 px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] text-[#E5C365]">
            {topRight}
          </span>
        </div>

        <div className="flex flex-col items-center gap-3">
          <div className="flex items-center gap-2 text-[#E5C365]/70">
            <Sparkles className="h-3 w-3" />
            <span className="h-px w-8 bg-[#D9A441]/40" />
            <Sparkles className="h-3 w-3" />
          </div>
          <div className="relative flex h-16 w-16 items-center justify-center rounded-full border border-[#D9A441]/40 bg-[#0B0C15]/60">
            <span className="absolute inset-0 rounded-full" style={{ boxShadow: "inset 0 0 18px rgba(217,164,65,0.25)" }} />
            <Icon className="h-8 w-8 text-[#D9A441]" strokeWidth={1.4} />
          </div>
          <div className="flex items-center gap-2 text-[#E5C365]/70">
            <Sparkles className="h-3 w-3" />
            <span className="h-px w-8 bg-[#D9A441]/40" />
            <Sparkles className="h-3 w-3" />
          </div>
        </div>

        <div className="w-full">
          <h3 className="font-display text-xl font-semibold leading-tight text-[#F8F3E6]">
            {card.title || card.name}
          </h3>
          {themeWord ? <p className="mt-1 font-display text-sm italic text-[#E5C365]">{themeWord}</p> : null}
          <p className="mt-1.5 text-[10px] uppercase leading-relaxed tracking-[0.15em] text-[#A3A5B8]">
            {(keywords || []).slice(0, 3).join(" · ")}
          </p>
        </div>

        {reversed && !isOracle && (
          <span
            className="absolute right-2 top-2 rounded-full bg-[#722F37]/70 px-2 py-0.5 text-[9px] font-medium tracking-wide text-[#F8F3E6]"
            style={{ transform: "rotate(180deg)" }}
          >
            Reversed
          </span>
        )}
      </div>
    </div>
  );
};

const CardBack = () => (
  <div className={FACE_BASE} style={{ transform: "rotateY(0deg)", border: "1px solid rgba(217,164,65,0.55)" }}>
    <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-lg" style={{ border: "1px dashed rgba(217,164,65,0.4)" }}>
      <div
        className="absolute inset-0 opacity-40"
        style={{ backgroundImage: `url('${CARD_BACK}')`, backgroundSize: "cover", backgroundPosition: "center", mixBlendMode: "screen" }}
      />
      <div className="absolute inset-0" style={{ background: "radial-gradient(circle, rgba(11,12,21,0.2) 0%, #0B0C15 90%)" }} />
      <div className="relative flex h-20 w-20 items-center justify-center rounded-full border border-[#D9A441]/50">
        <Sparkles className="h-8 w-8 text-[#D9A441]" strokeWidth={1.2} />
      </div>
    </div>
  </div>
);

export const TarotCard = ({
  card,
  reversed = false,
  flipped = false,
  deckType = "tarot",
  onClick,
  index = 0,
  testId,
}) => {
  const [imgError, setImgError] = useState(false);
  const [retry, setRetry] = useState(0);
  const clickable = Boolean(onClick);
  const useImage = Boolean(card.image) && !imgError;

  // Reset image state when the card changes (new draw reuses this component slot).
  useEffect(() => {
    setImgError(false);
    setRetry(0);
  }, [card.image]);

  // Cache-bust on retry so a stale cached 404 (e.g. image uploaded after first load)
  // self-heals instead of permanently pinning the card to the CSS fallback.
  const imgSrc = retry > 0 ? `${card.image}?r=${retry}` : card.image;
  const handleImgError = () => {
    if (retry < 2) setRetry((r) => r + 1);
    else setImgError(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: index * 0.12, duration: 0.5, ease: "easeOut" }}
      whileHover={clickable && !flipped ? { y: -10 } : {}}
      className={`relative aspect-[2/3.2] w-40 shrink-0 rounded-xl sm:w-48 md:w-52 ${
        clickable && !flipped ? "hover:shadow-[0_0_22px_rgba(217,164,65,0.4)]" : ""
      }`}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={onClick}
        disabled={!clickable}
        aria-label={flipped ? card.title || card.name : "Face-down card"}
        className="card-3d relative block h-full w-full rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-[#D9A441]"
      >
        {/* NOTE: never wrap this rotating subtree in a CSS filter/backdrop-filter —
            it flattens the 3D context and hides the front face after flip. */}
        <motion.div
          className="relative h-full w-full"
          style={{ transformStyle: "preserve-3d" }}
          animate={{ rotateY: flipped ? 180 : 0 }}
          transition={{ duration: 0.75, ease: "easeInOut" }}
        >
          <CardBack />
          {useImage ? (
            <ImageFront card={card} reversed={reversed} deckType={deckType} imgSrc={imgSrc} onError={handleImgError} />
          ) : (
            <CssFront card={card} reversed={reversed} deckType={deckType} />
          )}
        </motion.div>
      </button>
    </motion.div>
  );
};
